import { useEffect, useState } from 'react';
import type { B2BInvoice } from '@hospasense/contracts';
import { useAuth } from '../lib/auth.js';
import {
  fetchInvoices,
  fetchContracts,
  createInvoice,
  settleInvoice,
  voidInvoice,
  fetchOrganisationsList,
} from '../lib/queries-commercial.js';
import { CreateInvoiceModal, SettleInvoiceModal, VoidInvoiceModal } from './billing-modals.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  Badge,
  StatusPill,
  Table,
  TableContainer,
  THead,
  TBody,
  TR,
  TH,
  TD,
  LoadingState,
  ErrorState,
  PermissionDeniedState,
  useToast,
} from '../components/ui/index.js';

export function BillingPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const allowed = hasPermission('billing.read');
  const [invoices, setInvoices] = useState<B2BInvoice[]>([]);
  const [organisations, setOrganisations] = useState<Array<{ id: string; displayName: string }>>(
    [],
  );
  const [contracts, setContracts] = useState<
    Array<{ id: string; organisationId: string; contractNumber: string }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedInvoice, setSelectedInvoice] = useState<B2BInvoice | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);

  // Form states
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedContractId, setSelectedContractId] = useState('');
  const [subtotalInr, setSubtotalInr] = useState(300000);
  const [placeOfSupply, setPlaceOfSupply] = useState('KA');
  const [gstin, setGstin] = useState('29ABCDE1234F1Z5');
  const [recipientEmail, setRecipientEmail] = useState('accounts@cauvery.example');
  const [itemDescription, setItemDescription] = useState('Q1 Platform Subscription');
  const [paymentRef, setPaymentRef] = useState('NEFT-AXIS-88192039');
  const [voidReason, setVoidReason] = useState('Billing period updated');

  const loadData = async () => {
    try {
      setLoading(true);
      const [invData, orgsData, ctrData] = await Promise.all([
        fetchInvoices(),
        fetchOrganisationsList(),
        fetchContracts(),
      ]);
      setInvoices(invData.items);
      setOrganisations(orgsData);
      setContracts(
        ctrData.items.map((c) => ({
          id: c.id,
          organisationId: c.organisationId,
          contractNumber: c.contractNumber,
        })),
      );
      if (orgsData.length > 0) setSelectedOrgId(orgsData[0]!.id);
      if (ctrData.items.length > 0) setSelectedContractId(ctrData.items[0]!.id);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) void loadData();
  }, [allowed]);

  if (!allowed) return <PermissionDeniedState what="B2B Billing" />;

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createInvoice({
        organisationId: selectedOrgId,
        contractId: selectedContractId,
        billingPeriodStart: new Date().toISOString(),
        billingPeriodEnd: new Date(Date.now() + 90 * 24 * 3600 * 1000).toISOString(),
        dueDate: new Date(Date.now() + 15 * 24 * 3600 * 1000).toISOString(),
        subtotalInr,
        discountInr: 0,
        placeOfSupplyStateCode: placeOfSupply,
        customerGstin: gstin,
        recipientEmail,
        lineItems: [
          {
            description: itemDescription,
            quantity: 1,
            unitPriceInr: subtotalInr,
            discountAmountInr: 0,
          },
        ],
      });
      setShowCreateModal(false);
      await loadData();
    } catch (err) {
      toast.error(`Invoice generation failed: ${(err as Error).message}`);
    }
  };

  const handleSettle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    try {
      await settleInvoice(selectedInvoice.id, paymentRef, selectedInvoice.version);
      setShowSettleModal(false);
      setSelectedInvoice(null);
      await loadData();
    } catch (err) {
      toast.error(`Settlement failed: ${(err as Error).message}`);
    }
  };

  const handleVoid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;
    try {
      await voidInvoice(selectedInvoice.id, voidReason, selectedInvoice.version);
      setShowVoidModal(false);
      setSelectedInvoice(null);
      await loadData();
    } catch (err) {
      toast.error(`Void failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader
        title="B2B Platform Invoicing & GST"
        subtitle="SenseSys-to-hospital subscription billing, statutory GST tax invoices (SAC 998313), and settlement tracking"
        breadcrumbs={<Breadcrumbs items={[{ label: 'Control', to: '/' }, { label: 'Billing' }]} />}
        actions={
          hasPermission('billing.manage') ? (
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-invoice-button"
            >
              + Issue Tax Invoice
            </Button>
          ) : undefined
        }
      />

      {error && <ErrorState description={error} onRetry={loadData} />}

      {/* Invoices Table */}
      <TableContainer>
        <Table data-testid="invoices-table">
          <THead>
            <TR>
              <TH>Invoice #</TH>
              <TH>Organisation</TH>
              <TH>Place of Supply</TH>
              <TH>Taxable Amount</TH>
              <TH>Total (incl. GST)</TH>
              <TH>Status</TH>
              <TH>Actions</TH>
            </TR>
          </THead>
          <TBody>
            {loading ? (
              <TR>
                <TD colSpan={7} className="text-center py-6">
                  <LoadingState label="Loading invoices…" />
                </TD>
              </TR>
            ) : invoices.length === 0 ? (
              <TR>
                <TD colSpan={7} className="text-center text-muted-foreground py-6">
                  No B2B invoices found
                </TD>
              </TR>
            ) : (
              invoices.map((inv) => (
                <TR
                  key={inv.id}
                  onClick={() => setSelectedInvoice(inv)}
                  className="cursor-pointer hover:bg-surface-raised transition-colors"
                >
                  <TD className="font-medium text-foreground">{inv.invoiceNumber}</TD>
                  <TD className="font-medium text-foreground">
                    {inv.organisationName ?? inv.organisationId}
                  </TD>
                  <TD>
                    {inv.gstTax.placeOfSupplyStateCode}{' '}
                    {inv.gstTax.isInterState ? '(IGST 18%)' : '(CGST+SGST 18%)'}
                  </TD>
                  <TD>₹{inv.subtotalInr.toLocaleString('en-IN')}</TD>
                  <TD className="font-bold text-foreground">
                    ₹{inv.totalAmountInr.toLocaleString('en-IN')}
                  </TD>
                  <TD>
                    <StatusPill
                      tone={
                        inv.status === 'paid'
                          ? 'success'
                          : inv.status === 'issued'
                            ? 'info'
                            : inv.status === 'void'
                              ? 'neutral'
                              : 'error'
                      }
                      label={inv.status}
                    />
                  </TD>
                  <TD onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedInvoice(inv)}
                        data-testid={`view-invoice-${inv.id}`}
                      >
                        Details
                      </Button>
                      {inv.status === 'issued' && hasPermission('billing.manage') && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedInvoice(inv);
                            setShowSettleModal(true);
                          }}
                          data-testid={`settle-invoice-${inv.id}`}
                        >
                          Settle
                        </Button>
                      )}
                    </div>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>

      {/* Invoice Detail Drawer */}
      {selectedInvoice && (
        <Card data-testid="invoice-detail-drawer" className="space-y-4">
          <CardHeader
            title={`Tax Invoice ${selectedInvoice.invoiceNumber}`}
            actions={
              <Button variant="ghost" size="sm" onClick={() => setSelectedInvoice(null)}>
                Close
              </Button>
            }
          />
          <CardBody className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div>
                <span className="text-muted-foreground block">Customer GSTIN</span>
                <span className="font-medium text-foreground">
                  {selectedInvoice.customerGstin ?? 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">SAC Code</span>
                <span className="font-medium text-foreground">
                  {selectedInvoice.gstTax.hsnSacCode} (SaaS IT Services)
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Tax Breakdown</span>
                <span className="font-medium text-foreground">
                  {selectedInvoice.gstTax.isInterState
                    ? `IGST ₹${selectedInvoice.gstTax.igstAmountInr}`
                    : `CGST ₹${selectedInvoice.gstTax.cgstAmountInr} + SGST ₹${selectedInvoice.gstTax.sgstAmountInr}`}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground block">Total Due</span>
                <span className="font-bold text-foreground">
                  ₹{selectedInvoice.totalAmountInr.toLocaleString('en-IN')}
                </span>
              </div>
            </div>

            <div className="border-t border-border pt-3">
              <h4 className="text-xs font-semibold text-foreground mb-2">Line Items</h4>
              <div className="space-y-1">
                {selectedInvoice.lineItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between text-xs p-2 bg-surface-raised rounded border border-border"
                  >
                    <span>
                      {item.description} (Qty: {item.quantity})
                    </span>
                    <span className="font-mono">₹{item.amountInr.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>

            {selectedInvoice.paymentReference && (
              <Badge tone="success">
                Paid via {selectedInvoice.paymentReference} on{' '}
                {selectedInvoice.paidAt
                  ? new Date(selectedInvoice.paidAt).toLocaleDateString()
                  : 'N/A'}
              </Badge>
            )}

            <div className="flex justify-end gap-2 pt-2">
              {selectedInvoice.status === 'issued' && (
                <>
                  {hasPermission('billing.manage') && (
                    <Button
                      variant="primary"
                      onClick={() => setShowSettleModal(true)}
                      data-testid="settle-button"
                    >
                      Record Settlement
                    </Button>
                  )}
                  {hasPermission('billing.invoice.cancel') && (
                    <Button
                      variant="danger"
                      onClick={() => setShowVoidModal(true)}
                      data-testid="void-button"
                    >
                      Void Invoice
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      <CreateInvoiceModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateInvoice}
        organisations={organisations}
        contracts={contracts}
        selectedOrgId={selectedOrgId}
        setSelectedOrgId={setSelectedOrgId}
        selectedContractId={selectedContractId}
        setSelectedContractId={setSelectedContractId}
        itemDescription={itemDescription}
        setItemDescription={setItemDescription}
        subtotalInr={subtotalInr}
        setSubtotalInr={setSubtotalInr}
        placeOfSupply={placeOfSupply}
        setPlaceOfSupply={setPlaceOfSupply}
        gstin={gstin}
        setGstin={setGstin}
        recipientEmail={recipientEmail}
        setRecipientEmail={setRecipientEmail}
      />

      <SettleInvoiceModal
        open={showSettleModal && selectedInvoice !== null}
        onClose={() => setShowSettleModal(false)}
        onSubmit={handleSettle}
        paymentRef={paymentRef}
        setPaymentRef={setPaymentRef}
      />

      {selectedInvoice && (
        <VoidInvoiceModal
          open={showVoidModal}
          onClose={() => setShowVoidModal(false)}
          onSubmit={handleVoid}
          selectedInvoice={selectedInvoice}
          voidReason={voidReason}
          setVoidReason={setVoidReason}
        />
      )}
    </div>
  );
}
